<script lang="ts">
  import { authState } from '../../lib/auth.svelte';
  import { db } from '../../lib/firebase';
  import { disableUser, enableUser, queueInvitationEmail, type InvitationEmailData } from '../../lib/api';
  import { doc, getDoc, setDoc, collection, addDoc, query, where, getDocs, deleteDoc, updateDoc } from 'firebase/firestore';
  import { onMount } from 'svelte';
  import {
    type Role,
    type StaffMember,
    type Invitation,
    type Warehouse,
    type Business,
    ROLE_TO_USERTYPE,
    ROLE_LABELS,
    AVAILABLE_MODULES,
    DEFAULT_MODULES_BY_ROLE,
  } from '../../lib/types';

  // Access guard - AccountAdmin and SystemAdmin can access team management
  let hasAccess = $derived(!authState.loading && (authState.isAccountAdmin || authState.isSystemAdmin));

  // State
  let staffMembers = $state<StaffMember[]>([]);
  let pendingInvitations = $state<Invitation[]>([]);
  let warehouses = $state<Warehouse[]>([]);
  let businesses = $state<Business[]>([]);
  let loading = $state(true);
  let saveError = $state('');

  let showAddModal = $state(false);
  let showEditModal = $state(false);
  let showInviteLinkModal = $state(false);
  let showDisableModal = $state(false);
  let editingMember = $state<StaffMember | null>(null);
  let memberToDisable = $state<StaffMember | null>(null);
  let generatedInviteLink = $state('');
  let copiedToClipboard = $state(false);
  let disableLoading = $state(false);
  let emailSendStatus = $state<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  let emailSendError = $state('');

  // Form state
  let newEmail = $state('');
  let newName = $state('');
  let newRole = $state<Role>('staff');
  let newLocationId = $state<string>('');
  let newAllowedLocationIds = $state<string[]>([]);
  let newAllowedBusinessIds = $state<string[]>([]);
  let newAllowedModules = $state<string[]>([]);
  let formError = $state('');
  let formLoading = $state(false);

  // Derived: should show location selector (for staff/freight/businessAdmin)
  let showLocationSelector = $derived(['staff', 'freight', 'businessAdmin'].includes(newRole));

  // Get user document reference
  function getUserDocRef() {
    if (!authState.user) return null;
    return doc(db, 'users', authState.user.uid);
  }

  // Load staff members from Firestore
  async function loadStaffMembers() {
    const userDocRef = getUserDocRef();
    if (!userDocRef) return;

    try {
      const docSnap = await getDoc(userDocRef);

      if (docSnap.exists()) {
        const data = docSnap.data();

        if (data.staffMembers && Array.isArray(data.staffMembers)) {
          staffMembers = data.staffMembers;
        } else {
          // Initialize with current user as accountAdmin if no staff members exist
          staffMembers = [{
            id: authState.user!.uid,
            email: authState.user!.email || 'owner@example.com',
            name: 'Account Owner',
            role: 'accountAdmin',
            status: 'active',
            addedAt: new Date().toISOString(),
          }];
          await saveStaffMembers();
        }
      } else {
        // Create initial staff member (current user as accountAdmin)
        staffMembers = [{
          id: authState.user!.uid,
          email: authState.user!.email || 'owner@example.com',
          name: 'Account Owner',
          role: 'accountAdmin',
          status: 'active',
          addedAt: new Date().toISOString(),
        }];
        await saveStaffMembers();
      }

      // Load pending invitations
      await loadPendingInvitations();
    } catch (error: any) {
      console.error('Error loading staff members:', error);
      saveError = 'Failed to load team members';
    } finally {
      loading = false;
    }
  }

  // Load pending invitations from Firestore
  async function loadPendingInvitations() {
    if (!authState.user) return;

    try {
      const invitationsRef = collection(db, 'invitations');
      const q = query(
        invitationsRef,
        where('invitedBy', '==', authState.user.uid),
        where('status', '==', 'pending')
      );
      const snapshot = await getDocs(q);

      pendingInvitations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Invitation[];
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  }

  // Generate a random invite code
  function generateInviteCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let code = '';
    for (let i = 0; i < 12; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }

  // Get business info for invitation
  async function getBusinessInfo(): Promise<{ id: string | null; name: string }> {
    const userDocRef = getUserDocRef();
    if (!userDocRef) return { id: null, name: 'EQUOS Workspace' };

    try {
      const docSnap = await getDoc(userDocRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.businesses && data.businesses.length > 0) {
          const selectedId = data.selectedBusinessId;
          const business = selectedId
            ? data.businesses.find((b: any) => b.id === selectedId)
            : data.businesses[0];
          return {
            id: business?.id || null,
            name: business?.name || 'EQUOS Workspace',
          };
        }
      }
    } catch (error) {
      console.error('Error getting business info:', error);
    }
    return { id: null, name: 'EQUOS Workspace' };
  }

  // Save staff members to Firestore
  async function saveStaffMembers() {
    const userDocRef = getUserDocRef();
    if (!userDocRef) return;

    try {
      await setDoc(userDocRef, {
        staffMembers,
        updatedAt: new Date().toISOString(),
      }, { merge: true });

      saveError = '';
    } catch (error: any) {
      console.error('Error saving staff members:', error);
      saveError = 'Failed to save changes';
      throw error;
    }
  }

  // Load warehouses from user document
  async function loadWarehouses() {
    const userDocRef = getUserDocRef();
    if (!userDocRef) return;

    try {
      const docSnap = await getDoc(userDocRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Get warehouses from selected business or flatten all warehouses
        const selectedBizId = data.selectedBusinessId;
        if (data.warehouses && typeof data.warehouses === 'object') {
          if (selectedBizId && data.warehouses[selectedBizId]) {
            warehouses = data.warehouses[selectedBizId].filter((w: Warehouse) => w.isActive !== false);
          } else {
            // Flatten all warehouses from all businesses
            warehouses = Object.values(data.warehouses as Record<string, Warehouse[]>)
              .flat()
              .filter((w: Warehouse) => w.isActive !== false);
          }
        }
      }
    } catch (error) {
      console.error('Error loading warehouses:', error);
    }
  }

  // Load businesses from user document
  async function loadBusinesses() {
    const userDocRef = getUserDocRef();
    if (!userDocRef) return;

    try {
      const docSnap = await getDoc(userDocRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.businesses && Array.isArray(data.businesses)) {
          businesses = data.businesses.filter((b: Business) => b.isActive !== false);
        }
      }
    } catch (error) {
      console.error('Error loading businesses:', error);
    }
  }

  // Load data on mount
  onMount(() => {
    if (authState.user) {
      loadStaffMembers();
      loadWarehouses();
      loadBusinesses();
    } else {
      loading = false;
    }
  });

  function openAddModal() {
    newEmail = '';
    newName = '';
    newRole = 'staff';
    newLocationId = '';
    newAllowedLocationIds = [];
    newAllowedBusinessIds = [];
    newAllowedModules = [...DEFAULT_MODULES_BY_ROLE['staff']];
    formError = '';
    showAddModal = true;
  }

  // Update allowed modules when role changes
  function handleRoleChange(role: Role) {
    newRole = role;
    newAllowedModules = [...DEFAULT_MODULES_BY_ROLE[role]];
  }

  // Toggle a location in allowedLocationIds
  function toggleLocationAccess(locationId: string) {
    if (newAllowedLocationIds.includes(locationId)) {
      newAllowedLocationIds = newAllowedLocationIds.filter(id => id !== locationId);
    } else {
      newAllowedLocationIds = [...newAllowedLocationIds, locationId];
    }
  }

  // Toggle a business in allowedBusinessIds
  function toggleBusinessAccess(businessId: string) {
    if (newAllowedBusinessIds.includes(businessId)) {
      newAllowedBusinessIds = newAllowedBusinessIds.filter(id => id !== businessId);
    } else {
      newAllowedBusinessIds = [...newAllowedBusinessIds, businessId];
    }
  }

  // Toggle a module in allowedModules
  function toggleModule(moduleId: string) {
    if (newAllowedModules.includes(moduleId)) {
      newAllowedModules = newAllowedModules.filter(id => id !== moduleId);
    } else {
      newAllowedModules = [...newAllowedModules, moduleId];
    }
  }

  function closeAddModal() {
    showAddModal = false;
    formError = '';
  }

  function openEditModal(member: StaffMember) {
    editingMember = member;
    newEmail = member.email;
    newName = member.name;
    newRole = member.role;
    formError = '';
    showEditModal = true;
  }

  function closeEditModal() {
    showEditModal = false;
    editingMember = null;
    formError = '';
  }

  async function handleAddStaff(e: Event) {
    e.preventDefault();
    formError = '';

    if (!newEmail.trim() || !newName.trim()) {
      formError = 'Please fill in all fields';
      return;
    }

    // Check if already a staff member
    if (staffMembers.some(m => m.email.toLowerCase() === newEmail.toLowerCase())) {
      formError = 'A staff member with this email already exists';
      return;
    }

    // Check if already has pending invitation
    if (pendingInvitations.some(i => i.email.toLowerCase() === newEmail.toLowerCase())) {
      formError = 'An invitation has already been sent to this email';
      return;
    }

    formLoading = true;

    try {
      const inviteCode = generateInviteCode();
      const businessInfo = await getBusinessInfo();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // Expires in 7 days

      // Get selected location name if applicable
      const selectedLocation = warehouses.find(w => w.id === newLocationId);

      // Create invitation in Firestore with businessId for claims
      const invitationData = {
        email: newEmail.trim().toLowerCase(),
        name: newName.trim(),
        role: newRole,
        userType: ROLE_TO_USERTYPE[newRole],  // Flutter-compatible userType
        invitedBy: authState.user!.uid,
        inviterEmail: authState.user!.email,
        businessId: businessInfo.id,
        businessName: businessInfo.name,
        locationId: newLocationId || null,
        locationName: selectedLocation?.name || null,
        allowedLocationIds: newAllowedLocationIds.length > 0 ? newAllowedLocationIds : null,
        allowedBusinessIds: newAllowedBusinessIds.length > 0 ? newAllowedBusinessIds : null,
        allowedModules: newAllowedModules,
        createdAt: new Date().toISOString(),
        expiresAt: expiresAt.toISOString(),
        status: 'pending' as const,
      };

      const invitationsRef = collection(db, 'invitations');
      const docRef = await addDoc(invitationsRef, invitationData);

      // Add to local state
      pendingInvitations = [...pendingInvitations, { id: docRef.id, ...invitationData }];

      // Generate invite link (use /console/ path for production)
      const baseUrl = window.location.origin;
      const consolePath = import.meta.env.BASE_URL || '/console/';
      generatedInviteLink = `${baseUrl}${consolePath}#join?invite=${docRef.id}`;

      closeAddModal();
      showInviteLinkModal = true;
      copiedToClipboard = false;

      // Queue invitation email (non-blocking - don't fail if email queue fails)
      emailSendStatus = 'sending';
      emailSendError = '';

      const emailData: InvitationEmailData = {
        email: invitationData.email,
        name: invitationData.name,
        role: ROLE_LABELS[newRole],
        inviterName: authState.user?.displayName || authState.user?.email || 'Team Admin',
        inviterEmail: authState.user?.email || '',
        businessName: businessInfo.name,
        inviteLink: generatedInviteLink,
        expiresAt: expiresAt.toLocaleDateString('en-AU', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        // Include claim data for Firebase Auth user creation
        claimData: {
          businessId: invitationData.businessId,
          userType: invitationData.userType,
          allowedModules: invitationData.allowedModules,
          allowedLocationIds: invitationData.allowedLocationIds,
          allowedBusinessIds: invitationData.allowedBusinessIds,
          locationId: invitationData.locationId,
          teamOwnerId: authState.user!.uid,
        },
      };

      // Queue email to communications_queue collection for Cloud Function to process
      const emailResult = await queueInvitationEmail(emailData);

      if (emailResult.success) {
        emailSendStatus = 'sent';
      } else {
        emailSendStatus = 'failed';
        emailSendError = emailResult.error || 'Failed to queue email';
        console.error('Failed to queue invitation email:', emailResult.error);
      }
    } catch (error: any) {
      console.error('Error creating invitation:', error);
      formError = 'Failed to create invitation. Please try again.';
    } finally {
      formLoading = false;
    }
  }

  // Copy invite link to clipboard
  async function copyInviteLink() {
    try {
      await navigator.clipboard.writeText(generatedInviteLink);
      copiedToClipboard = true;
      setTimeout(() => {
        copiedToClipboard = false;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }

  // Close invite link modal
  function closeInviteLinkModal() {
    showInviteLinkModal = false;
    generatedInviteLink = '';
    emailSendStatus = 'idle';
    emailSendError = '';
  }

  // Revoke an invitation
  async function revokeInvitation(invitation: Invitation) {
    if (!confirm(`Revoke invitation for ${invitation.name}?`)) return;

    try {
      const inviteDocRef = doc(db, 'invitations', invitation.id);
      await updateDoc(inviteDocRef, { status: 'revoked' });

      pendingInvitations = pendingInvitations.filter(i => i.id !== invitation.id);
    } catch (error) {
      console.error('Error revoking invitation:', error);
      alert('Failed to revoke invitation. Please try again.');
    }
  }

  // Resend invitation (generate new link and send email)
  async function resendInvitation(invitation: Invitation) {
    await handleResendPendingInvitation(invitation);
  }

  async function handleEditStaff(e: Event) {
    e.preventDefault();
    if (!editingMember) return;

    formError = '';

    if (!newEmail.trim() || !newName.trim()) {
      formError = 'Please fill in all fields';
      return;
    }

    formLoading = true;

    try {
      staffMembers = staffMembers.map(m =>
        m.id === editingMember!.id
          ? { ...m, email: newEmail.trim(), name: newName.trim(), role: newRole }
          : m
      );

      await saveStaffMembers();
      closeEditModal();
    } catch (error: any) {
      formError = 'Failed to update team member. Please try again.';
    } finally {
      formLoading = false;
    }
  }

  async function handleRemoveStaff(member: StaffMember) {
    if (member.role === 'accountAdmin' && staffMembers.filter(m => m.role === 'accountAdmin').length === 1) {
      alert('Cannot remove the last account admin');
      return;
    }

    if (!confirm(`Remove ${member.name} from your team?`)) return;

    const previousMembers = [...staffMembers];

    try {
      staffMembers = staffMembers.filter(m => m.id !== member.id);
      await saveStaffMembers();
    } catch (error) {
      console.error('Failed to remove staff member:', error);
      staffMembers = previousMembers;
      alert('Failed to remove team member. Please try again.');
    }
  }

  async function handleResendInvite(member: StaffMember) {
    // Find the corresponding pending invitation for this staff member
    const invitation = pendingInvitations.find(i => i.email.toLowerCase() === member.email.toLowerCase());

    if (!invitation) {
      alert('No pending invitation found for this member. Please create a new invitation.');
      return;
    }

    await handleResendPendingInvitation(invitation);
  }

  // Resend email for a pending invitation
  async function handleResendPendingInvitation(invitation: Invitation) {
    emailSendStatus = 'sending';
    emailSendError = '';

    try {
      const baseUrl = window.location.origin;
      const consolePath = import.meta.env.BASE_URL || '/console/';
      const inviteLink = `${baseUrl}${consolePath}#join?invite=${invitation.id}`;

      const emailData: InvitationEmailData = {
        email: invitation.email,
        name: invitation.name,
        role: ROLE_LABELS[invitation.role as Role] || invitation.role,
        inviterName: authState.user?.displayName || authState.user?.email || 'Team Admin',
        inviterEmail: authState.user?.email || '',
        businessName: invitation.businessName,
        inviteLink: inviteLink,
        expiresAt: new Date(invitation.expiresAt).toLocaleDateString('en-AU', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        // Include claim data for Firebase Auth user creation on resend
        claimData: {
          businessId: invitation.businessId,
          userType: invitation.userType || ROLE_TO_USERTYPE[invitation.role as Role],
          allowedModules: invitation.allowedModules || [],
          allowedLocationIds: invitation.allowedLocationIds || null,
          allowedBusinessIds: null,
          locationId: invitation.locationId || null,
          teamOwnerId: invitation.invitedBy,
        },
      };

      // Queue email to communications_queue collection for Cloud Function to process
      const emailResult = await queueInvitationEmail(emailData);

      if (emailResult.success) {
        emailSendStatus = 'sent';
        // Also show the invite link modal
        generatedInviteLink = inviteLink;
        showInviteLinkModal = true;
        copiedToClipboard = false;
      } else {
        emailSendStatus = 'failed';
        emailSendError = emailResult.error || 'Failed to queue email';
        alert(`Failed to queue email: ${emailSendError}\n\nYou can still copy the invite link manually.`);
        // Show the link modal anyway
        generatedInviteLink = inviteLink;
        showInviteLinkModal = true;
        copiedToClipboard = false;
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      emailSendStatus = 'failed';
      emailSendError = error.message || 'Failed to queue email';
      alert(`Error resending invitation: ${error.message}`);
    }
  }

  // Open disable confirmation modal
  function openDisableModal(member: StaffMember) {
    memberToDisable = member;
    showDisableModal = true;
  }

  // Close disable modal
  function closeDisableModal() {
    showDisableModal = false;
    memberToDisable = null;
  }

  // Confirm and disable user
  async function confirmDisable() {
    if (!memberToDisable) return;

    disableLoading = true;
    try {
      await disableUser(memberToDisable.id);

      // Update local state
      staffMembers = staffMembers.map(m =>
        m.id === memberToDisable!.id ? { ...m, status: 'inactive' as const } : m
      );

      closeDisableModal();
    } catch (error: any) {
      console.error('Failed to disable user:', error);
      formError = error.message || 'Failed to disable user. Please try again.';
    } finally {
      disableLoading = false;
    }
  }

  // Enable a disabled user
  async function handleEnableUser(member: StaffMember) {
    try {
      await enableUser(member.id);

      // Update local state
      staffMembers = staffMembers.map(m =>
        m.id === member.id ? { ...m, status: 'active' as const } : m
      );
    } catch (error: any) {
      console.error('Failed to enable user:', error);
      alert(error.message || 'Failed to enable user. Please try again.');
    }
  }

  function getRoleBadgeClass(role: string): string {
    switch (role) {
      case 'accountAdmin': return 'role-admin';
      case 'businessAdmin': return 'role-business-admin';
      case 'client': return 'role-client';
      case 'freight': return 'role-freight';
      case 'account': return 'role-account';
      default: return 'role-staff';
    }
  }

  function getRoleLabel(role: Role): string {
    return ROLE_LABELS[role] || role;
  }

  function getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'active': return 'status-active';
      case 'pending': return 'status-pending';
      default: return 'status-inactive';
    }
  }
</script>

{#if authState.loading}
  <section class="staff-section">
    <div class="loading-state">
      <div class="spinner"></div>
      <p>Loading...</p>
    </div>
  </section>
{:else if !hasAccess}
  <section class="staff-section">
    <div class="access-denied">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
      <h3>Access Restricted</h3>
      <p>Team management is only available to account owners.</p>
    </div>
  </section>
{:else}
<section class="staff-section">
  <div class="section-header">
    <div>
      <h2>Team Members</h2>
      <p class="section-description">Manage who has access to your EQUOS workspace</p>
    </div>
    {#if !loading}
      <button class="btn-primary" onclick={openAddModal}>
        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
        </svg>
        Add Member
      </button>
    {/if}
  </div>

  {#if saveError}
    <div class="error-banner">{saveError}</div>
  {/if}

  {#if loading}
    <div class="loading-state">
      <div class="spinner"></div>
      <p>Loading team members...</p>
    </div>
  {:else}
  <div class="staff-list">
    <div class="staff-table-header">
      <span>Member</span>
      <span>Role</span>
      <span>Status</span>
      <span>Actions</span>
    </div>

    {#each staffMembers as member}
      <div class="staff-row {member.status === 'inactive' ? 'inactive' : ''}">
        <div class="member-info">
          <div class="avatar">
            {member.name.charAt(0).toUpperCase()}
          </div>
          <div class="member-details">
            <span class="member-name">{member.name}</span>
            <span class="member-email">{member.email}</span>
          </div>
        </div>

        <div class="member-role">
          <span class="role-badge {getRoleBadgeClass(member.role)}">
            {getRoleLabel(member.role)}
          </span>
        </div>

        <div class="member-status">
          <span class="status-badge {getStatusBadgeClass(member.status)}">
            {member.status}
          </span>
        </div>

        <div class="member-actions">
          {#if member.status === 'pending'}
            <button
              class="btn-icon"
              title="Resend invite"
              onclick={() => handleResendInvite(member)}
            >
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
            </button>
          {/if}
          {#if member.status === 'active' && member.id !== authState.user?.uid}
            <button
              class="btn-icon"
              title="Edit member"
              onclick={() => openEditModal(member)}
            >
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
            </button>
            <button
              class="btn-icon btn-warning"
              title="Disable account"
              onclick={() => openDisableModal(member)}
              disabled={member.role === 'accountAdmin' && staffMembers.filter(m => m.role === 'accountAdmin' && m.status === 'active').length === 1}
            >
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clip-rule="evenodd" />
              </svg>
            </button>
          {:else if member.status === 'inactive'}
            <button
              class="btn-icon btn-success"
              title="Enable account"
              onclick={() => handleEnableUser(member)}
            >
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </button>
          {:else if member.id !== authState.user?.uid}
            <button
              class="btn-icon"
              title="Edit member"
              onclick={() => openEditModal(member)}
            >
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
            </button>
          {/if}
          <button
            class="btn-icon btn-danger"
            title="Remove member"
            onclick={() => handleRemoveStaff(member)}
            disabled={member.role === 'accountAdmin' && staffMembers.filter(m => m.role === 'accountAdmin').length === 1}
          >
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    {/each}

    {#if staffMembers.length === 0 && pendingInvitations.length === 0}
      <div class="empty-state">
        <p>No team members yet. Add your first team member to get started.</p>
      </div>
    {/if}
  </div>

  {#if pendingInvitations.length > 0}
    <div class="pending-invitations">
      <h4>Pending Invitations ({pendingInvitations.length})</h4>
      <div class="invitations-list">
        {#each pendingInvitations as invitation}
          <div class="invitation-row">
            <div class="invitation-info">
              <div class="avatar pending">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </div>
              <div class="invitation-details">
                <span class="invitation-name">{invitation.name}</span>
                <span class="invitation-email">{invitation.email}</span>
              </div>
            </div>
            <div class="invitation-role">
              <span class="role-badge {getRoleBadgeClass(invitation.role)}">
                {getRoleLabel(invitation.role as Role)}
              </span>
            </div>
            <div class="invitation-expires">
              <span class="expires-text">
                Expires {new Date(invitation.expiresAt).toLocaleDateString()}
              </span>
            </div>
            <div class="invitation-actions">
              <button
                class="btn-icon"
                title="Copy invite link"
                onclick={() => resendInvitation(invitation)}
              >
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" />
                </svg>
              </button>
              <button
                class="btn-icon btn-danger"
                title="Revoke invitation"
                onclick={() => revokeInvitation(invitation)}
              >
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        {/each}
      </div>
    </div>
  {/if}

  <div class="roles-info">
    <h4>Role Permissions</h4>
    <ul>
      <li><strong>Account Admin</strong> - Full access including billing, team management, and all modules</li>
      <li><strong>Business Admin</strong> - Same as Account Admin but no billing or console access</li>
      <li><strong>Staff</strong> - Warehouse and Manufacturing modules only</li>
      <li><strong>Client</strong> - 3PL user: R/W inventory & orders, view freight, warehouse, manufacturing, billing</li>
      <li><strong>Freight</strong> - Transport module only</li>
      <li><strong>Account</strong> - Orders & Accounts R/W, read-only other modules</li>
    </ul>
  </div>
  {/if}
</section>
{/if}

<!-- Add Member Modal -->
{#if showAddModal}
  <div class="modal-overlay" onclick={closeAddModal}>
    <div class="modal" onclick={(e) => e.stopPropagation()}>
      <div class="modal-header">
        <h3>Add Team Member</h3>
        <button class="btn-close" onclick={closeAddModal}>&times;</button>
      </div>

      <form onsubmit={handleAddStaff}>
        {#if formError}
          <div class="form-error">{formError}</div>
        {/if}

        <div class="form-group">
          <label for="new-name">Name</label>
          <input
            id="new-name"
            type="text"
            bind:value={newName}
            placeholder="John Smith"
            disabled={formLoading}
          />
        </div>

        <div class="form-group">
          <label for="new-email">Email</label>
          <input
            id="new-email"
            type="email"
            bind:value={newEmail}
            placeholder="john@company.com"
            disabled={formLoading}
          />
        </div>

        <div class="form-group">
          <label for="new-role">Role</label>
          <select id="new-role" onchange={(e) => handleRoleChange(e.currentTarget.value as Role)} disabled={formLoading}>
            <option value="businessAdmin" selected={newRole === 'businessAdmin'}>Business Admin</option>
            <option value="staff" selected={newRole === 'staff'}>Staff</option>
            <option value="client" selected={newRole === 'client'}>Client</option>
            <option value="freight" selected={newRole === 'freight'}>Freight</option>
            <option value="account" selected={newRole === 'account'}>Account</option>
          </select>
          <small class="role-hint">
            {#if newRole === 'businessAdmin'}
              Same as Account Admin but no billing or console access
            {:else if newRole === 'staff'}
              Warehouse and Manufacturing modules only
            {:else if newRole === 'client'}
              3PL user: R/W inventory & orders, view other modules
            {:else if newRole === 'freight'}
              Transport module only
            {:else if newRole === 'account'}
              Orders & Accounts R/W, read-only other modules
            {/if}
          </small>
        </div>

        {#if businesses.length > 1}
          <div class="form-group">
            <label>Business Access</label>
            <div class="checkbox-group">
              {#each businesses as biz}
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newAllowedBusinessIds.includes(biz.id)}
                    onchange={() => toggleBusinessAccess(biz.id)}
                    disabled={formLoading}
                  />
                  {biz.name}
                </label>
              {/each}
            </div>
            <small class="role-hint">Select which businesses this team member can access. Leave unchecked for access to all businesses.</small>
          </div>
        {/if}

        {#if showLocationSelector && warehouses.length > 0}
          <div class="form-group">
            <label for="new-location">Primary Location</label>
            <select id="new-location" bind:value={newLocationId} disabled={formLoading}>
              <option value="">Select location...</option>
              {#each warehouses as wh}
                <option value={wh.id}>{wh.name}</option>
              {/each}
            </select>
            <small class="role-hint">Main warehouse location for this team member</small>
          </div>

          {#if warehouses.length > 1}
            <div class="form-group">
              <label>Restrict to Locations (optional)</label>
              <div class="checkbox-group">
                {#each warehouses as wh}
                  <label class="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newAllowedLocationIds.includes(wh.id)}
                      onchange={() => toggleLocationAccess(wh.id)}
                      disabled={formLoading}
                    />
                    {wh.name}
                  </label>
                {/each}
              </div>
              <small class="role-hint">Leave unchecked for access to all locations</small>
            </div>
          {/if}
        {/if}

        <div class="form-group">
          <label>Module Access</label>
          <div class="module-grid">
            {#each AVAILABLE_MODULES as mod}
              <label class="module-checkbox">
                <input
                  type="checkbox"
                  checked={newAllowedModules.includes(mod.id)}
                  onchange={() => toggleModule(mod.id)}
                  disabled={formLoading}
                />
                <span class="module-icon">{mod.icon}</span>
                <span>{mod.label}</span>
              </label>
            {/each}
          </div>
        </div>

        <p class="form-help">
          An invitation link will be generated for this team member.
        </p>

        <div class="modal-actions">
          <button type="button" class="btn-secondary" onclick={closeAddModal} disabled={formLoading}>
            Cancel
          </button>
          <button type="submit" class="btn-primary" disabled={formLoading}>
            {formLoading ? 'Sending...' : 'Send Invite'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- Edit Member Modal -->
{#if showEditModal && editingMember}
  <div class="modal-overlay" onclick={closeEditModal}>
    <div class="modal" onclick={(e) => e.stopPropagation()}>
      <div class="modal-header">
        <h3>Edit Team Member</h3>
        <button class="btn-close" onclick={closeEditModal}>&times;</button>
      </div>

      <form onsubmit={handleEditStaff}>
        {#if formError}
          <div class="form-error">{formError}</div>
        {/if}

        <div class="form-group">
          <label for="edit-name">Name</label>
          <input
            id="edit-name"
            type="text"
            bind:value={newName}
            placeholder="John Smith"
            disabled={formLoading}
          />
        </div>

        <div class="form-group">
          <label for="edit-email">Email</label>
          <input
            id="edit-email"
            type="email"
            bind:value={newEmail}
            placeholder="john@company.com"
            disabled={formLoading}
          />
        </div>

        <div class="form-group">
          <label for="edit-role">Role</label>
          <select id="edit-role" bind:value={newRole} disabled={formLoading}>
            <option value="accountAdmin">Account Admin</option>
            <option value="businessAdmin">Business Admin</option>
            <option value="staff">Staff</option>
            <option value="client">Client</option>
            <option value="freight">Freight</option>
            <option value="account">Account</option>
          </select>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn-secondary" onclick={closeEditModal} disabled={formLoading}>
            Cancel
          </button>
          <button type="submit" class="btn-primary" disabled={formLoading}>
            {formLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- Disable User Confirmation Modal -->
{#if showDisableModal && memberToDisable}
  <div class="modal-overlay" onclick={closeDisableModal}>
    <div class="modal" onclick={(e) => e.stopPropagation()}>
      <div class="modal-header">
        <h3>Disable User Account</h3>
        <button class="btn-close" onclick={closeDisableModal}>&times;</button>
      </div>

      <div class="modal-body">
        {#if formError}
          <div class="form-error">{formError}</div>
        {/if}

        <p>Are you sure you want to disable <strong>{memberToDisable.name}</strong>?</p>
        <p class="warning-text">They will no longer be able to sign in until their account is re-enabled.</p>
      </div>

      <div class="modal-actions" style="padding: 1.5rem;">
        <button class="btn-secondary" onclick={closeDisableModal} disabled={disableLoading}>
          Cancel
        </button>
        <button class="btn-danger-solid" onclick={confirmDisable} disabled={disableLoading}>
          {disableLoading ? 'Disabling...' : 'Disable Account'}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Invite Link Modal -->
{#if showInviteLinkModal}
  <div class="modal-overlay" onclick={closeInviteLinkModal}>
    <div class="modal" onclick={(e) => e.stopPropagation()}>
      <div class="modal-header">
        <h3>Invitation Created</h3>
        <button class="btn-close" onclick={closeInviteLinkModal}>&times;</button>
      </div>

      <div class="modal-body">
        <!-- Email Status -->
        {#if emailSendStatus === 'sending'}
          <div class="email-status sending">
            <div class="spinner-small"></div>
            <span>Queuing invitation email...</span>
          </div>
        {:else if emailSendStatus === 'sent'}
          <div class="email-status success">
            <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <span>Invitation email queued for delivery!</span>
          </div>
        {:else if emailSendStatus === 'failed'}
          <div class="email-status error">
            <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <span>Email failed: {emailSendError}</span>
          </div>
        {/if}

        <p style="margin-top: 1rem;">Share this link with the team member to complete their registration:</p>

        <div class="invite-link-container">
          <input type="text" readonly value={generatedInviteLink} class="invite-link-input" />
          <button class="btn-copy" onclick={copyInviteLink}>
            {#if copiedToClipboard}
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              Copied!
            {:else}
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M8 2a1 1 0 000 2h2a1 1 0 100-2H8z" />
                <path d="M3 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v6h-4.586l1.293-1.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L10.414 13H15v3a2 2 0 01-2 2H5a2 2 0 01-2-2V5zM15 11h2a1 1 0 110 2h-2v-2z" />
              </svg>
              Copy
            {/if}
          </button>
        </div>

        <p class="form-help" style="margin-top: 1rem;">
          The invitation expires in 7 days. You can resend the invitation if needed.
        </p>
      </div>

      <div class="modal-actions" style="padding: 1.5rem;">
        <button class="btn-primary" onclick={closeInviteLinkModal}>
          Done
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  /* Component-specific styles - shared styles are in /styles/components.css */

  .staff-section {
    background: var(--color-white);
    border-radius: var(--radius-md);
    padding: var(--space-6);
    box-shadow: var(--shadow-sm);
  }

  /* Access Denied State */
  .access-denied {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--space-12) var(--space-8);
    text-align: center;
    color: var(--color-gray-500);
  }

  .access-denied svg {
    width: 48px;
    height: 48px;
    color: var(--color-gray-400);
    margin-bottom: var(--space-4);
  }

  .access-denied h3 {
    margin: 0 0 var(--space-2);
    font-size: 1.1rem;
    color: var(--color-gray-800);
  }

  .access-denied p {
    margin: 0;
    font-size: 0.9rem;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: var(--space-6);
    gap: var(--space-4);
    flex-wrap: wrap;
  }

  .section-header h2 {
    margin: 0;
    font-size: 1.25rem;
    color: var(--color-gray-800);
  }

  .section-description {
    margin: var(--space-1) 0 0;
    color: var(--color-gray-500);
    font-size: 0.9rem;
  }

  .staff-list {
    border: 1px solid var(--color-gray-200);
    border-radius: var(--radius-md);
    overflow: hidden;
  }

  .staff-table-header {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 120px;
    gap: var(--space-4);
    padding: var(--space-3) var(--space-4);
    background: var(--color-gray-50);
    border-bottom: 1px solid var(--color-gray-200);
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--color-gray-500);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .staff-row {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 120px;
    gap: var(--space-4);
    padding: var(--space-4);
    align-items: center;
    border-bottom: 1px solid var(--color-gray-200);
  }

  .staff-row:last-child {
    border-bottom: none;
  }

  .staff-row:hover {
    background: var(--color-gray-50);
  }

  .member-info {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .member-details {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .member-name {
    font-weight: 500;
    color: var(--color-gray-800);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .member-email {
    font-size: 0.85rem;
    color: var(--color-gray-500);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Role-specific badge colors */
  .role-admin {
    background: var(--color-warning-bg);
    color: var(--color-warning-text);
  }

  .role-business-admin {
    background: var(--color-info-bg);
    color: var(--color-info-text);
  }

  .role-staff {
    background: var(--color-gray-200);
    color: var(--color-gray-700);
  }

  .role-client {
    background: var(--color-success-bg);
    color: var(--color-success-text);
  }

  .role-freight {
    background: #fce7f3;
    color: #9d174d;
  }

  .role-account {
    background: #ede9fe;
    color: #5b21b6;
  }

  .member-actions {
    display: flex;
    gap: var(--space-2);
    justify-content: flex-end;
  }

  /* Button icon color variants */
  .btn-icon.btn-warning:hover:not(:disabled) {
    background: var(--color-warning-bg);
    color: var(--color-warning);
  }

  .btn-icon.btn-success:hover:not(:disabled) {
    background: var(--color-success-bg);
    color: var(--color-success);
  }

  .roles-info {
    margin-top: var(--space-6);
    padding: var(--space-4);
    background: var(--color-gray-50);
    border-radius: var(--radius-md);
  }

  .roles-info h4 {
    margin: 0 0 var(--space-3);
    font-size: 0.9rem;
    color: var(--color-gray-800);
  }

  .roles-info ul {
    margin: 0;
    padding-left: var(--space-5);
    font-size: 0.85rem;
    color: var(--color-gray-500);
  }

  .roles-info li {
    margin-bottom: var(--space-2);
  }

  .roles-info li:last-child {
    margin-bottom: 0;
  }

  .role-hint {
    display: block;
    font-size: 0.8rem;
    color: var(--color-gray-500);
    margin-top: var(--space-2);
    font-style: italic;
  }

  /* Module permission grid */
  .module-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-2);
    padding: var(--space-3);
    background: var(--color-gray-50);
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-gray-200);
  }

  .module-checkbox {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2);
    background: var(--color-white);
    border-radius: 4px;
    font-size: 0.85rem;
    cursor: pointer;
    transition: background-color var(--transition-fast);
  }

  .module-checkbox:hover {
    background: var(--color-gray-100);
  }

  .module-checkbox input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  .module-icon {
    font-size: 1rem;
  }

  /* Checkbox group for locations */
  .checkbox-label {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: 0.9rem;
    cursor: pointer;
  }

  .checkbox-label input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  /* Danger button solid variant */
  .btn-danger-solid {
    padding: var(--space-3) var(--space-5);
    border-radius: var(--radius-sm);
    font-weight: 600;
    cursor: pointer;
    transition: all var(--transition-normal);
    border: none;
    font-size: 0.9rem;
    background: var(--color-error);
    color: var(--color-white);
  }

  .btn-danger-solid:hover:not(:disabled) {
    background: #b91c1c;
  }

  .btn-danger-solid:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Modal body extensions */
  .modal-body .warning-text {
    color: var(--color-warning-text);
    font-size: 0.9rem;
    margin: 0;
  }

  /* Inactive member styling */
  .staff-row.inactive {
    opacity: 0.6;
  }

  .staff-row.inactive .avatar {
    background: var(--color-gray-400);
  }

  .staff-row.inactive .member-name {
    color: var(--color-gray-500);
  }

  /* Invite Link Modal Styles */
  .invite-link-container {
    display: flex;
    gap: var(--space-2);
    margin-top: var(--space-3);
  }

  .invite-link-input {
    flex: 1;
    padding: var(--space-3);
    border: 1px solid var(--color-gray-200);
    border-radius: var(--radius-sm);
    font-size: 0.85rem;
    font-family: monospace;
    background: var(--color-gray-50);
  }

  .btn-copy {
    padding: var(--space-3) var(--space-4);
    background: var(--color-primary);
    color: var(--color-white);
    border: none;
    border-radius: var(--radius-sm);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-weight: 500;
    transition: background var(--transition-normal);
  }

  .btn-copy:hover {
    background: var(--color-primary-dark);
  }

  .btn-copy svg {
    width: 16px;
    height: 16px;
  }

  /* Email Status Styles */
  .email-status {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-sm);
    font-size: 0.9rem;
    margin-bottom: var(--space-2);
  }

  .email-status.sending {
    background: #e0e7ff;
    color: #3730a3;
  }

  .email-status.success {
    background: var(--color-success-bg);
    color: var(--color-success-text);
  }

  .email-status.error {
    background: var(--color-error-bg);
    color: var(--color-error-text);
  }

  .email-status svg {
    flex-shrink: 0;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .staff-table-header {
      display: none;
    }

    .staff-row {
      grid-template-columns: 1fr;
      gap: var(--space-3);
    }

    .member-role,
    .member-status {
      display: inline-block;
    }

    .member-actions {
      justify-content: flex-start;
      padding-top: var(--space-2);
      border-top: 1px solid var(--color-gray-200);
    }
  }
</style>
